import { useState, useEffect } from 'react'
import { HiUserPlus } from 'react-icons/hi2'
import { Input, Select, Button } from '../../../components'
import { BaseModal } from '../../../components/Modal/BaseModal'
import type { Setor } from '../../../models'
import type { CreateUserRequest } from '../types'

interface CreateUserModalProps {
  isOpen: boolean
  setores: Setor[]
  onClose: () => void
  onSubmit: (data: CreateUserRequest) => Promise<boolean>
}

const FUNCAO_OPTIONS = [
  { value: 'USUARIO', label: 'Usuário' },
  { value: 'DIRETOR', label: 'Diretor' },
  { value: 'ADMIN', label: 'Administrador' }
]

export const CreateUserModal = ({ isOpen, setores, onClose, onSubmit }: CreateUserModalProps) => {
  const [formData, setFormData] = useState<CreateUserRequest>({
    name: '',
    email: '',
    funcao_usuario: 'USUARIO',
    cargo_usuario: '',
    setor_logado_fk: undefined
  })
  const [errors, setErrors] = useState<Partial<Record<keyof CreateUserRequest, string>>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        email: '',
        funcao_usuario: 'USUARIO',
        cargo_usuario: ''
      })
      setErrors({})
    }
  }, [isOpen])

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CreateUserRequest, string>> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório'
    } else {
      const emailTrimmed = formData.email.trim()
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) {
        newErrors.email = 'Email inválido'
      }
    }

    if (!formData.cargo_usuario.trim()) {
      newErrors.cargo_usuario = 'Cargo é obrigatório'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    // Trim all string fields before submitting
    const dataToSubmit: CreateUserRequest = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      funcao_usuario: formData.funcao_usuario,
      cargo_usuario: formData.cargo_usuario.trim(),
      setor_logado_fk: formData.setor_logado_fk
    }

    setIsSubmitting(true)
    const success = await onSubmit(dataToSubmit)
    setIsSubmitting(false)

    if (success) {
      onClose()
    }
  }

  const handleChange = (field: keyof CreateUserRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const setorOptions = setores.map(setor => ({
    value: setor.id.toString(),
    label: setor.nome_setor
  }))

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Criar Novo Usuário"
      icon={<HiUserPlus className="w-6 h-6" />}
      description="Preencha os campos abaixo para criar um novo usuário no sistema"
      size="xl"
      useStandardLayout={true}
      resizable={true}
      stickyFooter={true}
      footerContent={
        <div className="flex items-center justify-end gap-3 p-4">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting}
            className="min-w-[140px]"
            onClick={handleSubmit}
          >
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Criando...
              </div>
            ) : (
              'Criar Usuário'
            )}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="p-4 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Nome */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nome Completo <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder="Ex: João da Silva"
                      disabled={isSubmitting}
                      className={errors.name ? 'border-red-500' : ''}
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email Corporativo <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder="nome@exemplo.com"
                      disabled={isSubmitting}
                      className={errors.email ? 'border-red-500' : ''}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      O login será feito via SSO (Azure AD) usando este email
                    </p>
                  </div>

                  {/* Cargo */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Cargo <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={formData.cargo_usuario}
                      onChange={(e) => handleChange('cargo_usuario', e.target.value)}
                      placeholder="Ex: Analista de Sistemas"
                      disabled={isSubmitting}
                      className={errors.cargo_usuario ? 'border-red-500' : ''}
                    />
                    {errors.cargo_usuario && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.cargo_usuario}</p>
                    )}
                  </div>

                  {/* Função */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Função <span className="text-red-500">*</span>
                    </label>
                    <Select
                      options={FUNCAO_OPTIONS}
                      value={formData.funcao_usuario}
                      onChange={(value) => handleChange('funcao_usuario', value)}
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Setor Logado */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Setor Logado (Opcional)
                    </label>
                    <Select
                      options={setorOptions}
                      value={formData.setor_logado_fk?.toString() || ''}
                      onChange={(value) => handleChange('setor_logado_fk', value ? parseInt(value as string) : undefined)}
                      placeholder="Selecione um setor..."
                      disabled={isSubmitting}
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Setor inicial do usuário. Pode ser alterado posteriormente.
                    </p>
                  </div>
                </div>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-900/50 rounded-lg p-4">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            <strong>Nota:</strong> Após criar o usuário, você poderá vincular ele a múltiplos setores através da opção &quot;Editar&quot;.
          </p>
        </div>
      </form>
    </BaseModal>
  )
}

